// app/src/features/submit/useSubmitWiring.js
import { useCallback, useMemo } from "react";
import { cancelForecast } from "../../commands/cancelForecast.js";
import { makeDefaultDeps } from "../../commands/deps.js";
import { useActiveForecast } from "../../hooks/useActiveForecast.js";
import { useForecastSubmit } from "../../hooks/useForecastSubmit.js";
import { useSession } from "../../hooks/useSession.js";
import { clearDraft, DRAFT_CONV_ID } from "../../lib/forecast/draft.js";
import { activeSet, comparisonCleared, entriesLoaded } from "../../lib/forecast/reducer.js";
import { selectCancellableRun } from "../../lib/forecast/selectors.js";
import {
  addDetachedRunId,
  PENDING_FORECAST_KEY,
  POST_TOUR_EXAMPLES_KEY,
} from "../../lib/forecast/storageKeys.js";
import { endDiscardedRunIntent } from "../../lib/telemetry/intentEmitter.js";

/**
 * The forecast submit and run lifecycle: the cancellable run, the soft cancel, CLEAR
 * (`resetForecast`), the submit command binding (`useForecastSubmit`, its argument object passed
 * unchanged — S-11) and "run this question as mine". Called where `AppInner` called
 * `useForecastSubmit`: after the bindings the submit reads (`autoZoom`, `blinkContract`, the tour
 * thunks) and before every consumer of `submit` / `resetForecast` (the chat intent, the tour
 * controller, the chat slot).
 *
 * The state and refs come from `useSubmitState`; the store reaches this hook as `dispatchRun` and
 * `runState` (injected, never imported — C-23). `setTourExamples` is the tour controller's setter,
 * which `AppInner` declares AFTER this hook and hands over through a render-synced forward ref: at
 * BASE `resetForecast` closed over the later binding and read it at CLEAR time. `multiwayEnabled`,
 * `autoQuestionTypeEnabled`, `forecastLiveProgressEnabled` and `guardForTos` are read from the
 * session context through `useSession()`; the active forecast through `useActiveForecast()`.
 *
 * @param {object} deps
 * @param {object} deps.runState  the run store's render-time state
 * @param {Function} deps.dispatchRun  the store's dispatch
 * @param {Function} deps.setTourExamples  the tour controller's setter (forward-ref'd by AppInner)
 */
export function useSubmitWiring({
  runState,
  dispatchRun,
  // AppInner's composer state (see useSubmitState on why it stays there)
  input,
  setInput,
  // useSubmitState's
  loading,
  activeTrace,
  setLoading,
  setForecastingPhase,
  setPendingPreprocess,
  setActiveTrace,
  forecastAbortRef,
  submitAbortRef,
  forecastErrorStreakRef,
  preprocessConfirmedRef,
  // resetForecast's and onCancelForecast's other inputs
  exampleRevealTimerRef,
  postTourNudgeTimerRef,
  setTourExamples,
  setChatThinking,
  setDebugPanelOpen,
  setContractOpen,
  setAnimPhase,
  setBranchAnim,
  // runQuestionAsMine's
  setTrackingSettingsId,
  // the rest of useForecastSubmit's argument object
  forecastEngine,
  compareMode,
  compareEngine,
  triggerLlm,
  compareTriggerLlm,
  debugMode,
  series,
  preprocessVariantId,
  setLastObs,
  setForecastEngine,
  slideCache,
  pushSayerMsgRef,
  contractNotePendingRef,
  lastTourPendingIdRef,
  postTourTipRef,
  postTourTipRunRef,
  pendingEngineRevertRef,
  autoZoom,
  blinkContract,
  startForecastAnim,
  fetchLiveSeries,
  clearSeriesSelection,
  reattachToForecast,
  getTourFixture,
  getTourBranchIncrease,
}) {
  const { multiwayEnabled, autoQuestionTypeEnabled, forecastLiveProgressEnabled, guardForTos } =
    useSession();
  const active = useActiveForecast();
  // The live (running) run within the active forecast's tree — the root itself
  // OR any of its branches. Drives the "✕ Cancel forecast" affordance so it
  // re-appears whenever the user re-opens a still-running forecast or branch from
  // the log (a CLEAR detaches without stopping, so runs keep going in the log).
  const cancellableRun = useMemo(
    () => selectCancellableRun(runState, active?.id ?? null),
    [runState, active?.id],
  );

  // Soft cancel only: flip the lifecycle row server-side via POST /api/forecasts/:trace_id/cancel +
  // abort the client's SSE fetch + clear the reload-resume handle. Chatboard's server-side cancel
  // handler also fans POST /forecast/cancel/:trace out to every configured
  // FORECAST_BACKEND_URL_<arm>, so the forecaster's agent loop is actually aborted. The command
  // recomputes the cancel target from the store (via the shared selectCancellableRun the
  // `canCancel` gate also uses), so only `activeTrace` (not in the store) needs to re-memoize this
  // binding.
  const onCancelForecast = useCallback(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization -- the dependency array is BASE's (App.jsx), kept verbatim: the three setters and forecastAbortRef arrive through the hook's argument object (stable); PKG-05 reconciles it
    () =>
      cancelForecast(
        {
          ...makeDefaultDeps(),
          ui: { setForecastingPhase, setLoading, setActiveTrace },
          refs: { forecastAbortRef },
        },
        { activeTrace },
      ),
    [activeTrace], // eslint-disable-line react-hooks/exhaustive-deps -- setForecastingPhase/setLoading/setActiveTrace are useState setters and forecastAbortRef a useRef (stable), siblings in AppInner at BASE and hook inputs here; the array is BASE's
  );

  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- the dependency array is BASE's (App.jsx), kept verbatim with its exhaustive-deps directive: the setters, refs and dispatchRun arrive through the hook's argument object; PKG-05 reconciles it
  const resetForecast = useCallback(() => {
    // Clear any lingering tour example list (and a pending reveal timer) so a
    // CLEAR doesn't show it AND the default empty-state Example Questions.
    if (exampleRevealTimerRef.current) {
      clearTimeout(exampleRevealTimerRef.current);
      exampleRevealTimerRef.current = null;
    }
    if (postTourNudgeTimerRef.current) {
      clearTimeout(postTourNudgeTimerRef.current);
      postTourNudgeTimerRef.current = null;
    }
    setTourExamples(false);
    try {
      sessionStorage.removeItem(POST_TOUR_EXAMPLES_KEY);
    } catch {
      /* ignore */
    }
    // CLEAR is a FULL DETACH of a RUNNING forecast, not a stop: it opens a fresh chat
    // (empty/example state) and detaches whatever is active from the chat WITHOUT
    // touching it. An in-flight forecast / branch keeps streaming and lands in the log
    // on its own; the user re-opens it from the log to view it (and the "✕ Cancel
    // forecast" affordance re-appears so they can stop it there). So we deliberately do
    // NOT abort forecastAbortRef / pendingAbortsRef and do NOT server-cancel a running run;
    // the run's own completion is focus-guarded (it won't steal the chat back unless it's
    // still the active run — see doForecast).
    //
    // The PREPROCESS phase is the one exception. A run still preprocessing has no trace,
    // no server-side row, and nothing to "land in the log" — but its in-flight preprocess
    // holds `submitAbortRef`, and when it resolves it re-drives doForecast/setPendingPreprocess
    // for the run the user just cleared, re-raising the GLOBAL `loading` guard (and re-popping
    // the confirmation panel). That stuck guard then silently drops the next chat submit
    // (`if (loading && !force) return`), so a new forecast can't start. Abort the preprocess
    // so submitForecast takes its `submitAc.signal.aborted` bail (runRemoved + return) instead
    // — leaving the app in a clean idle state. Safe once streaming: the completed submitAc has
    // nothing awaiting its signal, so the abort is a no-op there (and forecastAbortRef, the
    // stream, is untouched — the detach behavior above is preserved).
    submitAbortRef.current?.abort();
    submitAbortRef.current = null;
    endDiscardedRunIntent(active);
    setLoading(false);
    setChatThinking(false);
    setForecastingPhase(null);
    dispatchRun(activeSet(null));
    // Make the detach persist across a page reload. The run keeps running and
    // lands in the log (DB-backed), but a reload must NOT refocus it. Two parts:
    // (1) mark it DETACHED so the resume-sweep's "most-recent-running-root" focus
    //     fallback skips it (it still resumes passively → completes in the log);
    // (2) drop the single-slot resume handle (mirrors onCancelForecast).
    // Gate on `traceId`: the marker only has meaning for a run the resume sweep
    // could refocus, i.e. one that's persisted server-side and resumable — which
    // is exactly a run that has allocated a trace. A still-preprocessing run has no
    // trace, isn't in forecasts[], and was just aborted + removed above, so recording
    // its id would only leak a stale entry into DETACHED_RUNS_KEY for the tab's life.
    if (active?.id && active?.traceId) addDetachedRunId(active.id);
    try {
      sessionStorage.removeItem(PENDING_FORECAST_KEY);
    } catch {
      /* ignore */
    }
    setDebugPanelOpen(false);
    // CLEAR returns to a blank slate, and the plotted signals are part of what the
    // user sees — leaving them up stranded the detached forecast's signals on an
    // empty screen, where they then rode into whatever was submitted next. The
    // forecast's own selection is untouched on its row, so re-opening it from the
    // log restores exactly these signals (loadForecastSeries).
    clearSeriesSelection();
    // Does NOT delete the persisted forecast from the log — just clears the visible intro.
    dispatchRun(entriesLoaded(DRAFT_CONV_ID, []));
    clearDraft();
    // Also drop the comparison selection — otherwise CLEAR wipes the active
    // forecast/chat but compareRoots still resolves the previously-selected (or
    // URL-hydrated ?compare=a,b) pair, so the stacked lanes keep rendering and
    // the timeline never clears. Emptying it makes compareOn false (single-lane
    // render of the now-null active = blank), and the URL-sync effect drops the
    // ?compare param. compareMode (the Dual-Engine checkbox) is a user setting,
    // left as-is so the next run still honors it.
    dispatchRun(comparisonCleared());
    setContractOpen(false);
    setAnimPhase(null);
    setBranchAnim(null);
    setPendingPreprocess(null);
    setActiveTrace(null);
    // preprocessConfirmedRef is reset per submission, so a fresh submit() (which re-arms it to
    // false) and a subsequent CLEAR don't see a stale `true` from the previous cycle.
    preprocessConfirmedRef.current = false;
  }, [active, activeTrace, clearSeriesSelection]); // eslint-disable-line react-hooks/exhaustive-deps -- setAnimPhase/setBranchAnim are useState setters (stable); eslint can't infer stability through the custom hook boundary

  const { submit, doForecast } = useForecastSubmit({
    input,
    loading,
    forecastEngine,
    compareMode,
    compareEngine,
    triggerLlm,
    compareTriggerLlm,
    debugMode,
    series,
    multiwayEnabled,
    autoQuestionTypeEnabled,
    // The debug picker's per-session choice, "" when nothing is overridden. Only the AUTO
    // preprocess path can carry it (a variant IS an auto authoring strategy), and only a
    // debug-enabled deployment honours it — the server re-decides per request.
    preprocessVariantId,
    forecastLiveProgressEnabled,
    setInput,
    setLoading,
    setForecastingPhase,
    setPendingPreprocess,
    setActiveTrace,
    setLastObs,
    setForecastEngine,
    forecastAbortRef,
    submitAbortRef,
    slideCache,
    pushSayerMsgRef,
    forecastErrorStreakRef,
    preprocessConfirmedRef,
    contractNotePendingRef,
    lastTourPendingIdRef,
    postTourNudgeTimerRef,
    postTourTipRef,
    postTourTipRunRef,
    pendingEngineRevertRef,
    autoZoom,
    blinkContract,
    startForecastAnim,
    fetchLiveSeries,
    clearSeriesSelection,
    guardForTos,
    clearDraft,
    reattachToForecast,
    getTourFixture,
    getTourBranchIncrease,
  });

  // Declared HERE, below `useForecastSubmit`, because it needs `submit` in scope. It is an ORDINARY
  // SUBMIT of the same question, not the copy-link machinery: contract identity is a CONTENT HASH
  // and user-agnostic, so submitting the same question lands on the SAME question record by
  // construction. The submit creates their own row, branches, chat and schedule entry, while the
  // question record and its resolution verdict stay shared.
  const runQuestionAsMine = useCallback(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization -- the dependency array is BASE's (App.jsx), kept verbatim: setTrackingSettingsId arrives through the hook's argument object (stable); PKG-05 reconciles it
    (f) => {
      const q = f?.question || f?.contract?.question || f?.contract?.title || "";
      if (!q) return;
      setTrackingSettingsId(null); // the panel was the entry point; get out of the way
      submit(q);
    },
    [submit], // eslint-disable-line react-hooks/exhaustive-deps -- setTrackingSettingsId is a useState setter (stable), a sibling in AppInner at BASE and a hook input here; the array is BASE's
  );

  return { cancellableRun, onCancelForecast, resetForecast, submit, doForecast, runQuestionAsMine };
}
